//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import type { ExecutionTerminalEvent } from '../../../types/executionReceipt';

interface ExecutionTerminalNoticeProps {
  event: ExecutionTerminalEvent;
}

// What the notice says per terminal status. The run is NOT stopped by a refused
// call: it finishes, and this is the end-of-run report when no receipt card
// covers it. unverified: no receipt could judge the run, so no outcome is claimed;
// the refused calls (if any) are listed and the decision is the user's. A refusal
// alone never means a new plan is needed: only a receipt can say that.
// needs_new_plan is kept for events from a server that still sends it. Any other
// status falls back to a neutral title rather than guessing at wording.
const TITLE: Partial<Record<ExecutionTerminalEvent['status'], string>> = {
  needs_new_plan: 'A new plan is needed',
};

const titleFor = (event: ExecutionTerminalEvent): string => {
  if (event.status === 'unverified') {
    return event.refused_tools?.length > 0
      ? 'Some calls did not run: result not verified'
      : 'Execution finished: no receipt available';
  }
  return TITLE[event.status] ?? 'Execution ended';
};

const ExecutionTerminalNotice: React.FC<ExecutionTerminalNoticeProps> = ({
  event,
}) => (
  <Card
    className="my-1 rounded-lg border-2 border-amber-400 dark:border-amber-600"
    data-testid="execution-terminal-notice"
  >
    <CardContent className="p-4">
      <div className="mb-2 flex items-center gap-2">
        <AlertTriangle
          className="size-4 shrink-0 text-amber-600 dark:text-amber-400"
          aria-hidden="true"
        />
        <p className="text-sm font-bold">{titleFor(event)}</p>
      </div>
      <p className="mb-2 text-sm" data-testid="execution-terminal-message">
        {event.message}
      </p>
      {event.refused_tools?.length > 0 && (
        <p
          className="text-xs text-slate-500"
          data-testid="execution-terminal-refused-tools"
        >
          Did not run:{' '}
          {event.refused_tools.map((tool, i) => (
            <React.Fragment key={tool}>
              {i > 0 && ', '}
              <code className="rounded bg-gray-200 px-1 py-0.5 dark:bg-gray-700">
                {tool}
              </code>
            </React.Fragment>
          ))}
        </p>
      )}
    </CardContent>
  </Card>
);

export default ExecutionTerminalNotice;
